import { useQuery } from "@tanstack/react-query";
import { Cloud, CloudDrizzle, CloudFog, CloudLightning, CloudRain, CloudSnow, CloudSun, Moon, Sun, Wind } from "lucide-react";
import { fetchWeather } from "@/lib/api";
import { cn } from "@/lib/utils";

const iconFor = (code = "") => {
  const base = code.split("_")[0];
  const night = code.endsWith("_night");
  if (/thunder/.test(base)) return CloudLightning;
  if (/snow/.test(base)) return CloudSnow;
  if (/sleet/.test(base)) return CloudDrizzle;
  if (/heavyrain|^rain/.test(base)) return CloudRain;
  if (/lightrain/.test(base)) return CloudDrizzle;
  if (base === "fog") return CloudFog;
  if (base === "cloudy") return Cloud;
  if (base === "partlycloudy" || base === "fair") return night ? Moon : CloudSun;
  if (base === "clearsky") return night ? Moon : Sun;
  return Cloud;
};

/** Yr's forecast for the hour the doors open – only when the day is near enough for it to mean something (≤ 7 days). */
export const useEventWeather = (ev) => useQuery({
  queryKey: ["weather", ev?.date, ev?.start_time],
  queryFn: () => fetchWeather(ev.date, ev.start_time),
  enabled: !!ev?.date && Math.abs(new Date(`${ev.date}T00:00:00`) - new Date()) < 8 * 86_400_000,
  staleTime: 30 * 60_000, retry: false,
});

export const EventWeather = ({ ev, className }) => {
  const { data } = useEventWeather(ev);
  if (!data?.available) return null;
  const Icon = iconFor(data.symbol_code);
  const temp = data.temperature_c != null ? `${Math.round(data.temperature_c)}°` : null;
  return (
    <div className={cn("min-w-0", className)} data-testid="event-weather" title={`Vêrvarsel for Halsnøy – ${data.source}`}>
      <dt className="t-mono text-muted-foreground">Vêret</dt>
      <dd className="mt-1.5 flex items-center gap-2 text-[16px] font-medium leading-snug sm:text-[17px]">
        <Icon className="h-5 w-5 shrink-0 text-sol" strokeWidth={1.75} aria-hidden="true" />
        <span className="tabular" data-testid="event-weather-temp">{temp}</span>
        <span className="truncate text-foreground/80">{data.description}</span>
      </dd>
      <dd className="mt-0.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
        {data.precipitation_mm != null && data.precipitation_mm > 0 && <span className="tabular">{data.precipitation_mm} mm</span>}
        {data.wind_ms != null && <span className="inline-flex items-center gap-1 tabular"><Wind className="h-3 w-3" /> {Math.round(data.wind_ms)} m/s</span>}
        <span className="truncate">· Yr</span>
      </dd>
    </div>
  );
};
